import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import axios from 'axios'
import { Download, Edit2, X, Save, Users, Mail, Send, Trash2, Plus } from 'lucide-react'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'

const MemberManagement = () => {
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [yearFilter, setYearFilter] = useState('all')
  const [typeFilter, setTypeFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [tagFilter, setTagFilter] = useState('all')
  const [selectedMember, setSelectedMember] = useState(null)
  const [editingMember, setEditingMember] = useState(null)
  const [creatingMember, setCreatingMember] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [sortField, setSortField] = useState('name')
  const [sortDirection, setSortDirection] = useState('asc')
  const [selectedMembers, setSelectedMembers] = useState([])
  const [showBulkEmailModal, setShowBulkEmailModal] = useState(false)
  const [emailSubject, setEmailSubject] = useState('')
  const [emailMessage, setEmailMessage] = useState('')
  const [sendingEmail, setSendingEmail] = useState(false)
  const [syncingToContacts, setSyncingToContacts] = useState(false)
  const [primaryDateOfBirth, setPrimaryDateOfBirth] = useState(null)
  const [householdMembers, setHouseholdMembers] = useState([{ firstName: '', lastName: '', email: '', dateOfBirth: null }])
  const [createMode, setCreateMode] = useState('primary') // 'primary' or 'dependent'
  const [selectedPrimaryId, setSelectedPrimaryId] = useState('')
  const [dependentRelationship, setDependentRelationship] = useState('Spouse')
  const [editDateOfBirth, setEditDateOfBirth] = useState(null)
  const [editingDependentInfo, setEditingDependentInfo] = useState(null) // { primaryMemberId, householdIndex, name, email, relationship, tags }

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm()

  const watchMembershipType = watch('membershipType')

  // Fetch members
  const fetchMembers = async () => {
    try {
      setLoading(true)
      setError('')
      const response = await axios.get('/api/members')
      setMembers(response.data)
    } catch (err) {
      setError('Failed to load members. Please try again.')
      console.error('Error fetching members:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchMembers()
  }, [])

  // Calculate age from date of birth
  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return null
    const birthDate = new Date(dateOfBirth)
    const today = new Date()
    let age = today.getFullYear() - birthDate.getFullYear()
    const monthDiff = today.getMonth() - birthDate.getMonth()
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--
    }
    return age
  }

  // Expand members to include household members as individual rows
  const expandMembersWithHousehold = (members) => {
    const expandedRows = []

    members.forEach((member) => {
      // Calculate primary member's age from dateOfBirth
      const primaryAge = member.dateOfBirth ? calculateAge(member.dateOfBirth) : null

      // Add primary member
      expandedRows.push({
        ...member,
        age: primaryAge,
        isPrimary: true,
        isDependent: false,
        displayName: `${member.firstName} ${member.lastName}`,
        memberType: 'Primary Member'
      })

      // Add household members as individual rows
      if (member.householdMembers && member.householdMembers.length > 0) {
        member.householdMembers.forEach((householdMember, index) => {
          expandedRows.push({
            _id: `${member._id}_household_${index}`,
            primaryMemberId: member._id,
            householdIndex: index,
            firstName: householdMember.name?.split(' ')[0] || householdMember.name || '',
            lastName: householdMember.name?.split(' ').slice(1).join(' ') || '',
            displayName: householdMember.name || 'N/A',
            email: householdMember.email || member.email, // Use household member's email if provided, otherwise primary member's
            phone: member.phone, // Share primary member's phone
            address: member.address, // Share primary member's address
            membershipYear: member.membershipYear,
            membershipType: member.membershipType,
            membershipStatus: member.membershipStatus, // Share primary member's membership status
            isActive: member.isActive,
            isPrimary: false,
            isDependent: true,
            memberType: `Dependent (${householdMember.relationship || 'Family'})`,
            age: householdMember.age,
            relationship: householdMember.relationship,
            tags: householdMember.tags || [],
            primaryMemberName: `${member.firstName} ${member.lastName}`
          })
        })
      }
    })

    return expandedRows
  }

  // Handle edit member
  const handleEdit = (member) => {
    if (member.isDependent) {
      // Open dependent-specific edit modal
      const primaryMember = members.find(m => m._id === member.primaryMemberId)
      if (primaryMember) {
        const hm = primaryMember.householdMembers[member.householdIndex]
        setEditingDependentInfo({
          primaryMemberId: member.primaryMemberId,
          householdIndex: member.householdIndex,
          name: hm.name || '',
          email: hm.email || '',
          phone: hm.phone || '',
          relationship: hm.relationship || 'Family Member',
          tags: hm.tags || [],
        })
      }
    } else {
      setEditingMember(member)
      populateForm(member)
    }
  }

  // Handle save dependent edit
  const onSaveDependentEdit = async () => {
    if (!editingDependentInfo) return
    try {
      setSubmitting(true)
      setError('')

      const primaryMember = members.find(m => m._id === editingDependentInfo.primaryMemberId)
      if (!primaryMember) {
        setError('Primary member not found')
        setSubmitting(false)
        return
      }

      // Clone and update the specific household member
      const updatedHousehold = [...primaryMember.householdMembers]
      updatedHousehold[editingDependentInfo.householdIndex] = {
        ...updatedHousehold[editingDependentInfo.householdIndex],
        name: editingDependentInfo.name,
        email: editingDependentInfo.email,
        phone: editingDependentInfo.phone,
        relationship: editingDependentInfo.relationship,
        tags: editingDependentInfo.tags,
      }

      await axios.put(`/api/members/${editingDependentInfo.primaryMemberId}`, {
        householdMembers: updatedHousehold,
      })

      await fetchMembers()
      setEditingDependentInfo(null)
    } catch (err) {
      setError('Failed to update dependent. Please try again.')
      console.error('Error updating dependent:', err)
    } finally {
      setSubmitting(false)
    }
  }

  // Populate form with member data
  const populateForm = (member) => {
    setValue('firstName', member.firstName)
    setValue('lastName', member.lastName)
    setValue('email', member.email)
    setValue('phone', member.phone)
    setValue('street', member.address?.street || '')
    setValue('city', member.address?.city || '')
    setValue('state', member.address?.state || '')
    setValue('zipCode', member.address?.zipCode || '')
    setValue('membershipType', member.membershipType)
    setValue('membershipStatus', member.membershipStatus)
    setValue('membershipYear', member.membershipYear)
    setValue('isActive', member.isActive)
    setValue('paymentStatus', member.paymentStatus)
    setValue('amountPaid', member.amountPaid)
    setValue('notes', member.notes || '')
    setValue('tag_tennis_captain', member.tags?.includes('tennis_captain') || false)
    setValue('tag_board_member', member.tags?.includes('board_member') || false)
    setEditDateOfBirth(member.dateOfBirth ? new Date(member.dateOfBirth) : null)
  }

  // Handle save member (edit existing)
  const onSubmit = async (data) => {
    try {
      setSubmitting(true)
      setError('')

      const tags = []
      if (data.tag_tennis_captain) tags.push('tennis_captain')
      if (data.tag_board_member) tags.push('board_member')

      const updateData = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone,
        address: {
          street: data.street,
          city: data.city,
          state: data.state,
          zipCode: data.zipCode
        },
        membershipType: data.membershipType,
        membershipStatus: data.membershipStatus,
        membershipYear: parseInt(data.membershipYear),
        isActive: data.isActive,
        paymentStatus: data.paymentStatus,
        amountPaid: parseFloat(data.amountPaid),
        notes: data.notes,
        tags,
        dateOfBirth: editDateOfBirth ? editDateOfBirth.toISOString() : null,
      }

      await axios.put(`/api/members/${editingMember._id}`, updateData)

      await fetchMembers()
      setEditingMember(null)
      setEditDateOfBirth(null)
      reset()
    } catch (err) {
      setError('Failed to update member. Please try again.')
      console.error('Error updating member:', err)
    } finally {
      setSubmitting(false)
    }
  }

  // Handle create member
  const onCreateSubmit = async (data) => {
    try {
      setSubmitting(true)
      setError('')

      // Handle dependent mode - add to existing primary member's household
      if (createMode === 'dependent') {
        if (!selectedPrimaryId) {
          setError('Please select a primary member')
          setSubmitting(false)
          return
        }
        if (!data.firstName?.trim() || !data.lastName?.trim()) {
          setError('First and last name are required')
          setSubmitting(false)
          return
        }

        const primaryMember = members.find(m => m._id === selectedPrimaryId)
        if (!primaryMember) {
          setError('Selected primary member not found')
          setSubmitting(false)
          return
        }

        // Build the new household member entry
        const newDependent = {
          name: `${data.firstName.trim()} ${data.lastName.trim()}`,
          email: data.email?.trim() || '',
          phone: data.phone?.trim() || '',
          age: calculateAge(primaryDateOfBirth),
          relationship: dependentRelationship || 'Family Member',
        }

        // Append to existing household members
        const updatedHousehold = [...(primaryMember.householdMembers || []), newDependent]

        // Also ensure membership type is household
        await axios.put(`/api/members/${selectedPrimaryId}`, {
          householdMembers: updatedHousehold,
          membershipType: 'household',
        })

        await fetchMembers()
        setCreatingMember(false)
        reset()
        setPrimaryDateOfBirth(null)
        setCreateMode('primary')
        setSelectedPrimaryId('')
        setDependentRelationship('Spouse')
        return
      }

      // Primary member creation flow (existing logic)

      // Transform household members to match the format expected by the API
      // Filter out completely empty members and transform
      const transformedHouseholdMembers = data.membershipType === 'household'
        ? householdMembers
            .filter(member => member.firstName?.trim() || member.lastName?.trim())
            .map(member => ({
              firstName: (member.firstName || '').trim(),
              lastName: (member.lastName || '').trim(),
              email: member.email?.trim() || '',
              dateOfBirth: member.dateOfBirth ? member.dateOfBirth.toISOString().split('T')[0] : undefined
            }))
        : []

      const createData = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone,
        primaryDateOfBirth: primaryDateOfBirth ? primaryDateOfBirth.toISOString().split('T')[0] : undefined,
        address: data.street,
        city: data.city,
        state: data.state,
        zipCode: data.zipCode,
        membershipType: data.membershipType,
        memberStatus: data.membershipStatus,
        membershipYear: parseInt(data.membershipYear),
        totalAmount: parseFloat(data.amountPaid),
        emergencyContactName: data.emergencyContactName,
        emergencyContactPhone: data.emergencyContactPhone,
        emergencyContactRelationship: data.emergencyContactRelationship,
        agreeToRules: true,
        agreeToCommunications: true,
        agreeTolLiability: true,
        householdMembers: transformedHouseholdMembers,
        specialRequests: data.notes || ''
      }

      console.log('Creating member with data:', createData)
      console.log('Household members being sent:', transformedHouseholdMembers)

      // Create the member
      const response = await axios.post('/api/members', createData)

      // Update payment information since admin is creating manually
      await axios.put(`/api/members/${response.data.member._id}`, {
        paymentMethod: data.paymentMethod,
        paymentStatus: data.paymentStatus,
        isActive: data.isActive
      })

      await fetchMembers()
      setCreatingMember(false)
      reset()
      setPrimaryDateOfBirth(null)
      setHouseholdMembers([{ firstName: '', lastName: '', email: '', dateOfBirth: null }])
      setCreateMode('primary')
    } catch (err) {
      console.error('Full error object:', err)
      console.error('Error response:', err.response)
      console.error('Error response data:', err.response?.data)

      let errorMessage = 'Failed to create member. Please try again.'

      if (err.response?.data) {
        const data = err.response.data
        // Check various error formats
        if (typeof data === 'string') {
          errorMessage = data
        } else if (data.message) {
          errorMessage = data.message
        } else if (data.error) {
          errorMessage = data.error
        } else if (data.errors && Array.isArray(data.errors)) {
          // Handle validation errors
          errorMessage = data.errors.map(e => `${e.field}: ${e.message}`).join(', ')
        } else {
          // If we can't parse it, stringify it
          errorMessage = JSON.stringify(data)
        }
      } else if (err.message) {
        errorMessage = err.message
      }

      setError('Error creating member: ' + errorMessage)
      console.error('Parsed error message:', errorMessage)
    } finally {
      setSubmitting(false)
    }
  }

  // Add household member
  const addHouseholdMember = () => {
    setHouseholdMembers([...householdMembers, { firstName: '', lastName: '', email: '', dateOfBirth: null }])
  }

  // Remove household member
  const removeHouseholdMember = (index) => {
    const updated = householdMembers.filter((_, i) => i !== index)
    setHouseholdMembers(updated.length > 0 ? updated : [{ firstName: '', lastName: '', email: '', dateOfBirth: null }])
  }

  // Update household member
  const updateHouseholdMember = (index, field, value) => {
    const updated = [...householdMembers]
    updated[index] = { ...updated[index], [field]: value }
    setHouseholdMembers(updated)
  }

  // Handle delete member
  const handleDelete = async (member) => {
    // Don't allow deleting dependents (they're part of household members)
    if (member.isDependent) {
      alert('Cannot delete a dependent directly. Please edit the primary member to remove household members.')
      return
    }

    if (!confirm(`Are you sure you want to delete ${member.firstName} ${member.lastName}? This action cannot be undone.`)) {
      return
    }

    try {
      setError('')
      await axios.delete(`/api/members/${member._id}`)
      await fetchMembers()
    } catch (err) {
      setError('Failed to delete member. Please try again.')
      console.error('Error deleting member:', err)
    }
  }

  // Export to CSV
  const exportToCSV = () => {
    const csvRows = []
    const headers = [
      'Member Type',
      'Primary Member',
      'First Name',
      'Last Name',
      'Email',
      'Phone',
      'Street',
      'City',
      'State',
      'Zip Code',
      'Membership Type',
      'Membership Status',
      'Membership Year',
      'Active',
      'Payment Status',
      'Amount Paid',
      'Age',
      'Relationship'
    ]

    csvRows.push(headers.join(','))

    const expandedMembers = expandMembersWithHousehold(members)

    expandedMembers.forEach((row) => {
      const values = [
        row.memberType,
        row.isPrimary ? '' : row.primaryMemberName,
        row.firstName || '',
        row.lastName || '',
        row.email || '',
        row.phone || '',
        row.address?.street || '',
        row.address?.city || '',
        row.address?.state || '',
        row.address?.zipCode || '',
        row.membershipType || '',
        row.membershipStatus || '',
        row.membershipYear || '',
        row.isActive ? 'Yes' : 'No',
        row.isPrimary ? (row.paymentStatus || '') : '',
        row.isPrimary ? (row.amountPaid || '') : '',
        row.age || '',
        row.relationship || ''
      ]

      // Escape commas and quotes in values
      const escapedValues = values.map(value => {
        const stringValue = String(value)
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
          return `"${stringValue.replace(/"/g, '""')}"`
        }
        return stringValue
      })

      csvRows.push(escapedValues.join(','))
    })

    const csvContent = csvRows.join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `members_export_${new Date().toISOString().split('T')[0]}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  // Handle member selection for bulk email
  // Include primary members and dependents over 18
  const isEligibleForEmail = (member) => {
    if (member.isPrimary) return true
    if (member.isDependent && member.age >= 18) return true
    return false
  }

  const toggleMemberSelection = (memberId) => {
    setSelectedMembers(prev =>
      prev.includes(memberId)
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    )
  }

  const toggleSelectAll = () => {
    const eligibleMembers = filteredMembers.filter(m => isEligibleForEmail(m))
    if (selectedMembers.length === eligibleMembers.length) {
      setSelectedMembers([])
    } else {
      setSelectedMembers(eligibleMembers.map(m => m._id))
    }
  }

  // Send bulk email
  const handleSendBulkEmail = async () => {
    try {
      setSendingEmail(true)
      setError('')

      // Get selected member emails
      // For primary members, use their email directly
      // For dependents, use the primary member's email (already included in the dependent row)
      const selectedExpandedMembers = expandedMembers.filter(m => selectedMembers.includes(m._id))
      const emailList = [...new Set(selectedExpandedMembers.map(m => m.email))] // Remove duplicates

      await axios.post('/api/members/bulk-email', {
        recipients: emailList,
        subject: emailSubject,
        message: emailMessage
      })

      alert('Bulk email sent successfully!')
      setShowBulkEmailModal(false)
      setEmailSubject('')
      setEmailMessage('')
      setSelectedMembers([])
    } catch (err) {
      setError('Failed to send bulk email. Please try again.')
      console.error('Error sending bulk email:', err)
    } finally {
      setSendingEmail(false)
    }
  }

  // Sync to Google Contacts
  const handleSyncToGoogleContacts = async () => {
    try {
      setSyncingToContacts(true)
      setError('')

      const response = await axios.post('/api/members/sync-to-contacts')

      const { message, results } = response.data
      const summary = results
        ? `${message}\n\nCreated: ${results.created}\nUpdated: ${results.updated}\nFailed: ${results.failed}`
        : message

      alert(summary)

      if (results && results.errors && results.errors.length > 0) {
        console.error('Sync errors:', results.errors)
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to sync to Google Contacts. Please try again.'
      setError(errorMsg)
      console.error('Error syncing to Google Contacts:', err)
      alert(errorMsg)
    } finally {
      setSyncingToContacts(false)
    }
  }

  // Expand members and filter
  const expandedMembers = expandMembersWithHousehold(members)

  const filteredMembers = expandedMembers
    .filter((member) => {
      // Search filter
      if (searchTerm) {
        const term = searchTerm.toLowerCase()
        const fullName = member.displayName.toLowerCase()
        const primaryName = member.primaryMemberName?.toLowerCase() || ''
        const address = member.address
          ? `${member.address.street} ${member.address.city} ${member.address.state} ${member.address.zipCode}`.toLowerCase()
          : ''
        return (
          fullName.includes(term) ||
          primaryName.includes(term) ||
          member.email?.toLowerCase().includes(term) ||
          member.phone?.toLowerCase().includes(term) ||
          address.includes(term)
        )
      }
      return true
    })
    .filter((member) => {
      // Year filter
      if (yearFilter !== 'all') {
        return member.membershipYear?.toString() === yearFilter
      }
      return true
    })
    .filter((member) => {
      // Type filter
      if (typeFilter !== 'all') {
        return member.membershipType?.toLowerCase() === typeFilter.toLowerCase()
      }
      return true
    })
    .filter((member) => {
      // Status filter (based on isActive and membershipStatus)
      if (statusFilter !== 'all') {
        if (statusFilter === 'active') {
          return member.isActive === true
        } else if (statusFilter === 'inactive') {
          return member.isActive === false
        } else if (statusFilter === 'new' || statusFilter === 'returning') {
          return member.membershipStatus?.toLowerCase() === statusFilter.toLowerCase()
        }
      }
      return true
    })
    .filter((member) => {
      // Tag filter
      if (tagFilter !== 'all') {
        return member.tags?.includes(tagFilter)
      }
      return true
    })
    .sort((a, b) => {
      // Sorting
      let aVal, bVal

      // Handle special fields that don't exist in the model
      if (sortField === 'name') {
        aVal = a.displayName.toLowerCase()
        bVal = b.displayName.toLowerCase()
      } else if (sortField === 'year') {
        aVal = a.membershipYear || ''
        bVal = b.membershipYear || ''
      } else if (sortField === 'status') {
        aVal = a.membershipStatus || ''
        bVal = b.membershipStatus || ''
      } else {
        aVal = a[sortField] || ''
        bVal = b[sortField] || ''
      }

      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase()
        bVal = typeof bVal === 'string' ? bVal.toLowerCase() : bVal
      }

      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0
      } else {
        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0
      }
    })

  // Handle sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  // Get unique years for filter
  const uniqueYears = [...new Set(members.map((m) => m.membershipYear).filter(Boolean))].sort(
    (a, b) => b - a
  )

  // Get status badge
  const getStatusBadge = (status) => {
    const badges = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-800',
      pending: 'bg-yellow-100 text-yellow-800',
    }
    return badges[status?.toLowerCase()] || 'bg-gray-100 text-gray-800'
  }

  // Get type badge
  const getTypeBadge = (type) => {
    const badges = {
      household: 'bg-primary-100 text-primary-800',
      single: 'bg-secondary-100 text-secondary-800',
      senior: 'bg-tertiary-100 text-tertiary-800',
    }
    return badges[type?.toLowerCase()] || 'bg-gray-100 text-gray-800'
  }

  // Sort icon
  const SortIcon = ({ field }) => {
    if (sortField !== field) {
      return (
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
          />
        </svg>
      )
    }
    return sortDirection === 'asc' ? (
      <svg className="w-4 h-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Member Management</h1>
          <p className="text-gray-600 mt-1">View and manage club membership records (includes household members)</p>
        </div>
        <div className="flex gap-3 flex-wrap">
          <button
            onClick={() => {
              setCreatingMember(true)
              setCreateMode('primary')
              setSelectedPrimaryId('')
              setDependentRelationship('Spouse')
              reset({
                membershipYear: new Date().getFullYear(),
                paymentMethod: 'cash',
                paymentStatus: 'completed',
                isActive: true
              })
              setPrimaryDateOfBirth(null)
              setHouseholdMembers([{ firstName: '', lastName: '', email: '', dateOfBirth: null }])
            }}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center shadow-lg"
          >
            <Plus className="w-5 h-5 mr-2" />
            Create Member
          </button>
          {selectedMembers.length > 0 && (
            <button
              onClick={() => setShowBulkEmailModal(true)}
              className="px-4 py-2 bg-secondary-600 text-white rounded-lg hover:bg-secondary-700 font-medium flex items-center shadow-lg"
            >
              <Mail className="w-5 h-5 mr-2" />
              Send Email ({selectedMembers.length})
            </button>
          )}
          <button
            onClick={handleSyncToGoogleContacts}
            disabled={syncingToContacts}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium flex items-center shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Users className="w-5 h-5 mr-2" />
            {syncingToContacts ? 'Syncing...' : 'Sync to Google Contacts'}
          </button>
          <button
            onClick={exportToCSV}
            className="px-4 py-2 bg-tertiary-600 text-white rounded-lg hover:bg-tertiary-700 font-medium flex items-center shadow-lg"
          >
            <Download className="w-5 h-5 mr-2" />
            Export CSV
          </button>
          <button
            onClick={fetchMembers}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium flex items-center shadow-lg"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          {/* Search */}
          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg
                  className="h-5 w-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search by name, email, phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
              />
            </div>
          </div>

          {/* Year Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Year</label>
            <select
              value={yearFilter}
              onChange={(e) => setYearFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
            >
              <option value="all">All Years</option>
              {uniqueYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          {/* Type Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
            >
              <option value="all">All Types</option>
              <option value="household">Household</option>
              <option value="single">Single</option>
              <option value="senior">Senior</option>
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="pending">Pending</option>
            </select>
          </div>

          {/* Tag Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
            <select
              value={tagFilter}
              onChange={(e) => setTagFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
            >
              <option value="all">All Roles</option>
              <option value="tennis_captain">Tennis Captain</option>
              <option value="board_member">Board Member</option>
            </select>
          </div>
        </div>

        {/* Results Count */}
        <div className="mt-4 flex items-center gap-3">
          <div className="text-sm text-gray-600">
            Showing {filteredMembers.length} of {expandedMembers.length} rows
          </div>
          <div className="text-sm text-gray-500">
            ({(() => {
              const now = new Date()
              const currentYear = now.getMonth() < 4 ? now.getFullYear() - 1 : now.getFullYear()
              const active = members.filter(m =>
                m.isActive &&
                m.paymentStatus === 'completed' &&
                (m.membershipYear === currentYear || m.membershipYear === currentYear + 1)
              )
              const total = active.reduce((c, m) => c + 1 + (m.householdMembers?.length || 0), 0)
              return `${active.length} active primary / ${total} total w/ dependents`
            })()})
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Members Table */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : filteredMembers.length === 0 ? (
          <div className="text-center py-12">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No members found</h3>
            <p className="mt-1 text-sm text-gray-500">
              No members match your search criteria.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedMembers.length === filteredMembers.filter(m => isEligibleForEmail(m)).length && filteredMembers.filter(m => isEligibleForEmail(m)).length > 0}
                      onChange={toggleSelectAll}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                  </th>
                  <th
                    onClick={() => handleSort('name')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center gap-1">
                      Name
                      <SortIcon field="name" />
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Member Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    DOB / Age
                  </th>
                  <th
                    onClick={() => handleSort('year')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center gap-1">
                      Year
                      <SortIcon field="year" />
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('membershipType')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center gap-1">
                      Type
                      <SortIcon field="membershipType" />
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('status')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center gap-1">
                      Status
                      <SortIcon field="status" />
                    </div>
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredMembers.map((member) => (
                  <tr key={member._id} className={`hover:bg-gray-50 ${member.isDependent ? 'bg-primary-50/30' : ''}`}>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {isEligibleForEmail(member) && (
                        <input
                          type="checkbox"
                          checked={selectedMembers.includes(member._id)}
                          onChange={() => toggleMemberSelection(member._id)}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className={`flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center ${member.isDependent ? 'bg-tertiary-100' : 'bg-primary-100'}`}>
                          <span className={`font-semibold text-sm ${member.isDependent ? 'text-tertiary-600' : 'text-primary-600'}`}>
                            {member.displayName?.charAt(0) || 'M'}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {member.displayName || 'N/A'}
                          </div>
                          {member.tags && member.tags.length > 0 && (
                            <div className="flex gap-1 mt-0.5">
                              {member.tags.includes('tennis_captain') && (
                                <span className="px-1.5 py-0.5 text-xs font-medium rounded bg-amber-100 text-amber-800">Captain</span>
                              )}
                              {member.tags.includes('board_member') && (
                                <span className="px-1.5 py-0.5 text-xs font-medium rounded bg-indigo-100 text-indigo-800">Board</span>
                              )}
                            </div>
                          )}
                          {member.isDependent && (
                            <div className="text-xs text-gray-500">
                              under {member.primaryMemberName}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${member.isPrimary ? 'bg-primary-100 text-primary-800' : 'bg-tertiary-100 text-tertiary-800'}`}>
                        {member.memberType}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{member.email || 'N/A'}</div>
                      <div className="text-sm text-gray-500">{member.phone || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {member.isPrimary && member.dateOfBirth ? (
                        <div>
                          <div className="text-sm text-gray-900">
                            {new Date(member.dateOfBirth).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}
                          </div>
                          <div className="text-sm text-gray-500">Age: {calculateAge(member.dateOfBirth)}</div>
                        </div>
                      ) : member.isDependent && member.age != null ? (
                        <div className="text-sm text-gray-500">Age: {member.age}</div>
                      ) : (
                        <div className="text-sm text-gray-400">—</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{member.membershipYear || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getTypeBadge(
                          member.membershipType
                        )}`}
                      >
                        {member.membershipType?.charAt(0).toUpperCase() +
                          member.membershipType?.slice(1) || 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadge(
                          member.isActive ? 'active' : 'inactive'
                        )}`}
                      >
                        {member.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-3">
                        <button
                          onClick={() => handleEdit(member)}
                          className="text-primary-600 hover:text-primary-900 flex items-center gap-1"
                        >
                          <Edit2 className="h-4 w-4" />
                          Edit
                        </button>
                        <button
                          onClick={() => setSelectedMember(member)}
                          className="text-gray-600 hover:text-gray-900"
                        >
                          View
                        </button>
                        {member.isPrimary && (
                          <button
                            onClick={() => handleDelete(member)}
                            className="text-red-600 hover:text-red-900 flex items-center gap-1"
                          >
                            <Trash2 className="h-4 w-4" />
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Member Details Modal */}
      {selectedMember && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-900">Member Details</h3>
              <button
                onClick={() => setSelectedMember(null)}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="px-6 py-6 space-y-6">
              {/* Basic Info */}
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Name</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {`${selectedMember.firstName} ${selectedMember.lastName}` || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Email</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedMember.email || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Phone</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedMember.phone || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Date of Birth</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {selectedMember.isPrimary && selectedMember.dateOfBirth
                        ? `${new Date(selectedMember.dateOfBirth).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })} (Age: ${calculateAge(selectedMember.dateOfBirth)})`
                        : selectedMember.isDependent && selectedMember.age != null
                        ? `Age: ${selectedMember.age}`
                        : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Year</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedMember.membershipYear || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Membership Info */}
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Membership</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Type</label>
                    <p className="mt-1">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getTypeBadge(
                          selectedMember.membershipType
                        )}`}
                      >
                        {selectedMember.membershipType?.charAt(0).toUpperCase() +
                          selectedMember.membershipType?.slice(1) || 'N/A'}
                      </span>
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Status</label>
                    <p className="mt-1">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadge(
                          selectedMember.isActive ? 'active' : 'inactive'
                        )}`}
                      >
                        {selectedMember.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Member Type</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {selectedMember.membershipStatus?.charAt(0).toUpperCase() +
                        selectedMember.membershipStatus?.slice(1) || 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Address */}
              {selectedMember.address && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Address</h4>
                  <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-lg">
                    {selectedMember.address.street}, {selectedMember.address.city}, {selectedMember.address.state} {selectedMember.address.zipCode}
                  </p>
                </div>
              )}

              {/* Emergency Contact */}
              {selectedMember.emergencyContact && selectedMember.isPrimary && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Emergency Contact</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Name</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedMember.emergencyContact.name || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Phone</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedMember.emergencyContact.phone || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Relationship</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedMember.emergencyContact.relationship || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Terms Acceptance */}
              {selectedMember.termsAcceptance && selectedMember.isPrimary && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Terms & Agreement Acceptance</h4>
                  <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                    <div className="flex items-center">
                      <div className={`w-5 h-5 rounded flex items-center justify-center mr-3 ${selectedMember.termsAcceptance.agreedToRules ? 'bg-green-500' : 'bg-red-500'}`}>
                        {selectedMember.termsAcceptance.agreedToRules ? (
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        )}
                      </div>
                      <span className="text-sm text-gray-900">Agreed to Club Rules & Regulations</span>
                    </div>
                    <div className="flex items-center">
                      <div className={`w-5 h-5 rounded flex items-center justify-center mr-3 ${selectedMember.termsAcceptance.agreedToCommunications ? 'bg-green-500' : 'bg-red-500'}`}>
                        {selectedMember.termsAcceptance.agreedToCommunications ? (
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        )}
                      </div>
                      <span className="text-sm text-gray-900">Agreed to Communications</span>
                    </div>
                    <div className="flex items-center">
                      <div className={`w-5 h-5 rounded flex items-center justify-center mr-3 ${selectedMember.termsAcceptance.agreedToLiability ? 'bg-green-500' : 'bg-red-500'}`}>
                        {selectedMember.termsAcceptance.agreedToLiability ? (
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        )}
                      </div>
                      <span className="text-sm text-gray-900">Agreed to Liability Waiver</span>
                    </div>
                    {selectedMember.termsAcceptance.acceptedAt && (
                      <div className="pt-3 mt-3 border-t border-gray-200">
                        <label className="text-sm font-medium text-gray-500">Accepted On</label>
                        <p className="mt-1 text-sm text-gray-900">
                          {new Date(selectedMember.termsAcceptance.acceptedAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Additional Info */}
              {(selectedMember.createdAt || selectedMember.notes) && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Additional Information</h4>
                  <div className="grid grid-cols-1 gap-4">
                    {selectedMember.createdAt && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Signup Date</label>
                        <p className="mt-1 text-sm text-gray-900">
                          {new Date(selectedMember.createdAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })}
                        </p>
                      </div>
                    )}
                    {selectedMember.notes && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Notes</label>
                        <p className="mt-1 text-sm text-gray-900 bg-gray-50 p-3 rounded-lg">
                          {selectedMember.notes}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Member Modal */}
      {editingMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 overflow-y-auto p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full my-8">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">
                Edit Member: {editingMember.firstName} {editingMember.lastName}
              </h2>
              <button
                onClick={() => {
                  setEditingMember(null)
                  setEditDateOfBirth(null)
                  reset()
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
              {/* Basic Info */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
                    <input type="text" {...register('firstName', { required: true })} className="input" />
                    {errors.firstName && <p className="text-red-600 text-sm mt-1">Required</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                    <input type="text" {...register('lastName', { required: true })} className="input" />
                    {errors.lastName && <p className="text-red-600 text-sm mt-1">Required</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                    <input type="email" {...register('email', { required: true })} className="input" />
                    {errors.email && <p className="text-red-600 text-sm mt-1">Required</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                    <input type="tel" {...register('phone', { required: true })} className="input" />
                    {errors.phone && <p className="text-red-600 text-sm mt-1">Required</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Date of Birth</label>
                    <DatePicker
                      selected={editDateOfBirth}
                      onChange={(date) => setEditDateOfBirth(date)}
                      dateFormat="MM/dd/yyyy"
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      yearDropdownItemNumber={100}
                      scrollableYearDropdown
                      maxDate={new Date()}
                      placeholderText="MM/DD/YYYY"
                      className="input w-full"
                    />
                  </div>
                </div>
              </div>

              {/* Address */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Address</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Street</label>
                    <input type="text" {...register('street', { required: true })} className="input" />
                    {errors.street && <p className="text-red-600 text-sm mt-1">Required</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                    <input type="text" {...register('city', { required: true })} className="input" />
                    {errors.city && <p className="text-red-600 text-sm mt-1">Required</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                    <input type="text" {...register('state', { required: true })} className="input" />
                    {errors.state && <p className="text-red-600 text-sm mt-1">Required</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Zip Code</label>
                    <input type="text" {...register('zipCode', { required: true })} className="input" />
                    {errors.zipCode && <p className="text-red-600 text-sm mt-1">Required</p>}
                  </div>
                </div>
              </div>

              {/* Membership Info */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Membership</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Membership Type</label>
                    <select {...register('membershipType', { required: true })} className="input">
                      <option value="household">Household</option>
                      <option value="single">Single</option>
                      <option value="senior">Senior</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Membership Status</label>
                    <select {...register('membershipStatus', { required: true })} className="input">
                      <option value="new">New</option>
                      <option value="returning">Returning</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Membership Year</label>
                    <input type="number" {...register('membershipYear', { required: true })} className="input" />
                    {errors.membershipYear && <p className="text-red-600 text-sm mt-1">Required</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Amount Paid</label>
                    <input type="number" step="0.01" {...register('amountPaid', { required: true })} className="input" />
                    {errors.amountPaid && <p className="text-red-600 text-sm mt-1">Required</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Payment Status</label>
                    <select {...register('paymentStatus', { required: true })} className="input">
                      <option value="pending">Pending</option>
                      <option value="completed">Completed</option>
                      <option value="failed">Failed</option>
                    </select>
                  </div>
                  <div className="flex items-center pt-6">
                    <input type="checkbox" {...register('isActive')} className="h-4 w-4 text-primary-600 border-gray-300 rounded" />
                    <label className="ml-2 text-sm text-gray-700">Active Membership</label>
                  </div>
                </div>
              </div>

              {/* Roles & Tags */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Roles & Tags</h3>
                <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                  <div className="flex items-center">
                    <input type="checkbox" {...register('tag_tennis_captain')} className="h-4 w-4 text-amber-600 border-gray-300 rounded" />
                    <label className="ml-2 text-sm text-gray-700">Tennis Captain</label>
                  </div>
                  <div className="flex items-center">
                    <input type="checkbox" {...register('tag_board_member')} className="h-4 w-4 text-indigo-600 border-gray-300 rounded" />
                    <label className="ml-2 text-sm text-gray-700">Board Member</label>
                  </div>
                  <p className="text-xs text-gray-500">Captains and board members can book courts beyond 2 weeks and create recurring reservations.</p>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                <textarea {...register('notes')} rows={3} className="input" />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setEditingMember(null)
                    setEditDateOfBirth(null)
                    reset()
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" disabled={submitting} className="btn btn-primary flex items-center gap-2">
                  <Save className="h-4 w-4" />
                  {submitting ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Dependent Modal */}
      {editingDependentInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 overflow-y-auto p-4">
          <div className="bg-white rounded-lg max-w-lg w-full my-8">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">Edit Dependent</h2>
              <button
                onClick={() => setEditingDependentInfo(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                <input
                  type="text"
                  value={editingDependentInfo.name}
                  onChange={(e) => setEditingDependentInfo({ ...editingDependentInfo, name: e.target.value })}
                  className="input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  value={editingDependentInfo.email}
                  onChange={(e) => setEditingDependentInfo({ ...editingDependentInfo, email: e.target.value })}
                  className="input"
                  placeholder="email@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                <input
                  type="tel"
                  value={editingDependentInfo.phone}
                  onChange={(e) => setEditingDependentInfo({ ...editingDependentInfo, phone: e.target.value })}
                  className="input"
                  placeholder="(555) 555-5555"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Relationship</label>
                <select
                  value={editingDependentInfo.relationship}
                  onChange={(e) => setEditingDependentInfo({ ...editingDependentInfo, relationship: e.target.value })}
                  className="input"
                >
                  <option value="Spouse">Spouse</option>
                  <option value="Child">Child</option>
                  <option value="Parent">Parent</option>
                  <option value="Sibling">Sibling</option>
                  <option value="Family Member">Family Member</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Roles & Tags */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Roles & Tags</label>
                <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={editingDependentInfo.tags.includes('tennis_captain')}
                      onChange={(e) => {
                        const tags = e.target.checked
                          ? [...editingDependentInfo.tags, 'tennis_captain']
                          : editingDependentInfo.tags.filter(t => t !== 'tennis_captain')
                        setEditingDependentInfo({ ...editingDependentInfo, tags })
                      }}
                      className="h-4 w-4 text-amber-600 border-gray-300 rounded"
                    />
                    <label className="ml-2 text-sm text-gray-700">Tennis Captain</label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={editingDependentInfo.tags.includes('board_member')}
                      onChange={(e) => {
                        const tags = e.target.checked
                          ? [...editingDependentInfo.tags, 'board_member']
                          : editingDependentInfo.tags.filter(t => t !== 'board_member')
                        setEditingDependentInfo({ ...editingDependentInfo, tags })
                      }}
                      className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                    />
                    <label className="ml-2 text-sm text-gray-700">Board Member</label>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setEditingDependentInfo(null)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={onSaveDependentEdit}
                  disabled={submitting}
                  className="btn btn-primary flex items-center gap-2"
                >
                  <Save className="h-4 w-4" />
                  {submitting ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Email Modal */}
      {showBulkEmailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 overflow-y-auto p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full my-8">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">
                Send Bulk Email ({selectedMembers.length} members)
              </h2>
              <button
                onClick={() => setShowBulkEmailModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Subject */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subject <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  className="input"
                  placeholder="Enter email subject"
                />
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={emailMessage}
                  onChange={(e) => setEmailMessage(e.target.value)}
                  rows={8}
                  className="input"
                  placeholder="Enter your message here..."
                />
              </div>

              {/* Recipients Info */}
              <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                <p className="text-sm text-primary-900">
                  <strong>Recipients:</strong> This email will be sent to {selectedMembers.length} primary member{selectedMembers.length !== 1 ? 's' : ''}.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowBulkEmailModal(false)}
                  className="btn btn-secondary"
                  disabled={sendingEmail}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendBulkEmail}
                  disabled={!emailSubject || !emailMessage || sendingEmail}
                  className="btn btn-primary flex items-center gap-2"
                >
                  <Send className="h-4 w-4" />
                  {sendingEmail ? 'Sending...' : 'Send Email'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Member Modal */}
      {creatingMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 overflow-y-auto p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full my-8">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">
                {createMode === 'dependent' ? 'Add Dependent Member' : 'Create New Member'}
              </h2>
              <button
                onClick={() => {
                  setCreatingMember(false)
                  reset()
                  setPrimaryDateOfBirth(null)
                  setCreateMode('primary')
                  setSelectedPrimaryId('')
                  setDependentRelationship('Spouse')
                  setHouseholdMembers([{ firstName: '', lastName: '', email: '', dateOfBirth: null }])
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit(onCreateSubmit)} className="p-6 space-y-6">
              {/* Primary / Dependent Toggle */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Member Role</label>
                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={() => setCreateMode('primary')}
                    className={`flex-1 py-3 px-4 rounded-lg border-2 text-center font-medium transition-colors ${
                      createMode === 'primary'
                        ? 'border-primary-600 bg-primary-50 text-primary-700'
                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-base">Primary Member</div>
                    <div className="text-xs font-normal mt-1 text-gray-500">New standalone membership</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setCreateMode('dependent')}
                    className={`flex-1 py-3 px-4 rounded-lg border-2 text-center font-medium transition-colors ${
                      createMode === 'dependent'
                        ? 'border-tertiary-600 bg-tertiary-50 text-tertiary-700'
                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-base">Dependent</div>
                    <div className="text-xs font-normal mt-1 text-gray-500">Add to existing member's household</div>
                  </button>
                </div>
              </div>

              {/* Dependent Mode: Select Primary Member */}
              {createMode === 'dependent' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Primary Member
                    </label>
                    <select
                      value={selectedPrimaryId}
                      onChange={(e) => setSelectedPrimaryId(e.target.value)}
                      className="input"
                    >
                      <option value="">-- Select primary member --</option>
                      {members
                        .filter(m => m.isActive)
                        .sort((a, b) => `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`))
                        .map(m => (
                          <option key={m._id} value={m._id}>
                            {m.lastName}, {m.firstName} — {m.membershipType} ({m.membershipYear})
                          </option>
                        ))
                      }
                    </select>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Dependent Information</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
                        <input type="text" {...register('firstName')} className="input" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                        <input type="text" {...register('lastName')} className="input" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                        <input type="email" {...register('email')} className="input" placeholder="email@example.com" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                        <input type="tel" {...register('phone')} className="input" placeholder="(555) 555-5555" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Date of Birth</label>
                        <DatePicker
                          selected={primaryDateOfBirth}
                          onChange={(date) => setPrimaryDateOfBirth(date)}
                          dateFormat="MM/dd/yyyy"
                          showMonthDropdown
                          showYearDropdown
                          dropdownMode="select"
                          yearDropdownItemNumber={100}
                          scrollableYearDropdown
                          maxDate={new Date()}
                          placeholderText="MM/DD/YYYY"
                          className="input w-full"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Relationship</label>
                        <select
                          value={dependentRelationship}
                          onChange={(e) => setDependentRelationship(e.target.value)}
                          className="input"
                        >
                          <option value="Spouse">Spouse</option>
                          <option value="Child">Child</option>
                          <option value="Parent">Parent</option>
                          <option value="Sibling">Sibling</option>
                          <option value="Family Member">Family Member</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {selectedPrimaryId && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-sm text-blue-900">
                        {(() => {
                          const pm = members.find(m => m._id === selectedPrimaryId)
                          if (!pm) return ''
                          const existingCount = pm.householdMembers?.length || 0
                          return (
                            <>
                              <strong>Adding to:</strong> {pm.firstName} {pm.lastName}'s household.
                              {existingCount > 0 && ` Currently has ${existingCount} household member${existingCount !== 1 ? 's' : ''}.`}
                              {pm.membershipType !== 'household' && (
                                <> Membership type will be updated to "Household".</>
                              )}
                            </>
                          )
                        })()}
                      </p>
                    </div>
                  )}
                </>
              )}

              {/* Primary Mode: Full Form */}
              {createMode === 'primary' && (
                <>
                  {/* Basic Info */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
                        <input type="text" {...register('firstName')} className="input" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                        <input type="text" {...register('lastName')} className="input" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                        <input type="email" {...register('email')} className="input" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                        <input type="tel" {...register('phone')} className="input" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Date of Birth</label>
                        <DatePicker
                          selected={primaryDateOfBirth}
                          onChange={(date) => setPrimaryDateOfBirth(date)}
                          dateFormat="MM/dd/yyyy"
                          showMonthDropdown
                          showYearDropdown
                          dropdownMode="select"
                          yearDropdownItemNumber={100}
                          scrollableYearDropdown
                          maxDate={new Date()}
                          placeholderText="MM/DD/YYYY"
                          className="input w-full"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Address */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Address</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Street</label>
                        <input type="text" {...register('street')} className="input" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                        <input type="text" {...register('city')} className="input" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                        <input type="text" {...register('state')} className="input" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Zip Code</label>
                        <input type="text" {...register('zipCode')} className="input" />
                      </div>
                    </div>
                  </div>

                  {/* Emergency Contact */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Emergency Contact</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                        <input type="text" {...register('emergencyContactName')} className="input" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                        <input type="tel" {...register('emergencyContactPhone')} className="input" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Relationship</label>
                        <input type="text" {...register('emergencyContactRelationship')} className="input" />
                      </div>
                    </div>
                  </div>

                  {/* Membership Info */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Membership</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Membership Type</label>
                        <select {...register('membershipType')} className="input">
                          <option value="household">Household</option>
                          <option value="single">Single</option>
                          <option value="senior">Senior</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Membership Status</label>
                        <select {...register('membershipStatus')} className="input">
                          <option value="new">New</option>
                          <option value="returning">Returning</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Membership Year</label>
                        <input type="number" {...register('membershipYear')} className="input" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Amount Paid</label>
                        <input type="number" step="0.01" {...register('amountPaid')} className="input" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method</label>
                        <select {...register('paymentMethod')} className="input">
                          <option value="cash">Cash</option>
                          <option value="check">Check</option>
                          <option value="square">Square (Online)</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Payment Status</label>
                        <select {...register('paymentStatus')} className="input">
                          <option value="pending">Pending</option>
                          <option value="completed">Completed</option>
                          <option value="failed">Failed</option>
                        </select>
                      </div>
                      <div className="flex items-center pt-6">
                        <input type="checkbox" {...register('isActive')} className="h-4 w-4 text-primary-600 border-gray-300 rounded" />
                        <label className="ml-2 text-sm text-gray-700">Active Membership</label>
                      </div>
                    </div>
                  </div>

                  {/* Household Members */}
                  {watchMembershipType === 'household' && (
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">Household Members</h3>
                        <button
                          type="button"
                          onClick={addHouseholdMember}
                          className="btn btn-secondary flex items-center gap-2"
                        >
                          <Plus className="h-4 w-4" />
                          Add Member
                        </button>
                      </div>
                      <div className="space-y-4">
                        {householdMembers.map((member, index) => (
                          <div key={index} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="font-medium text-gray-900">Member {index + 1}</h4>
                              {householdMembers.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => removeHouseholdMember(index)}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <X className="h-5 w-5" />
                                </button>
                              )}
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  First Name
                                </label>
                                <input
                                  type="text"
                                  value={member.firstName}
                                  onChange={(e) => updateHouseholdMember(index, 'firstName', e.target.value)}
                                  className="input"
                                  placeholder="First name"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Last Name
                                </label>
                                <input
                                  type="text"
                                  value={member.lastName}
                                  onChange={(e) => updateHouseholdMember(index, 'lastName', e.target.value)}
                                  className="input"
                                  placeholder="Last name"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Email Address
                                </label>
                                <input
                                  type="email"
                                  value={member.email}
                                  onChange={(e) => updateHouseholdMember(index, 'email', e.target.value)}
                                  className="input"
                                  placeholder="email@example.com"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Date of Birth
                                </label>
                                <DatePicker
                                  selected={member.dateOfBirth}
                                  onChange={(date) => updateHouseholdMember(index, 'dateOfBirth', date)}
                                  dateFormat="MM/dd/yyyy"
                                  showMonthDropdown
                                  showYearDropdown
                                  dropdownMode="select"
                                  yearDropdownItemNumber={100}
                                  scrollableYearDropdown
                                  maxDate={new Date()}
                                  placeholderText="MM/DD/YYYY"
                                  className="input w-full"
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Notes */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                    <textarea {...register('notes')} rows={3} className="input" placeholder="Any additional notes or comments..." />
                  </div>

                  {/* Info Notice */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-900">
                      <strong>Note:</strong> This member will be automatically marked as having agreed to all club terms and regulations.
                      The acceptance date will be set to the current time.
                    </p>
                  </div>
                </>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setCreatingMember(false)
                    reset()
                    setPrimaryDateOfBirth(null)
                    setCreateMode('primary')
                    setSelectedPrimaryId('')
                    setDependentRelationship('Spouse')
                    setHouseholdMembers([{ firstName: '', lastName: '', email: '', dateOfBirth: null }])
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" disabled={submitting} className="btn btn-primary flex items-center gap-2">
                  <Save className="h-4 w-4" />
                  {submitting
                    ? (createMode === 'dependent' ? 'Adding...' : 'Creating...')
                    : (createMode === 'dependent' ? 'Add Dependent' : 'Create Member')
                  }
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default MemberManagement
